import React, { useMemo, useState } from 'react'
import { AnalyticsMonthPoint, AnalyticsPoint } from '../lib/api'

const COLOR_CARDS = '#2557A7'
const COLOR_OPENS = '#16A085'

type Props = {
  data: AnalyticsPoint[] | AnalyticsMonthPoint[]
  xKey: 'date' | 'month'
}

const W = 800
const H = 220
const PAD_L = 40
const PAD_R = 10
const PAD_T = 20
const PAD_B = 30
const INNER_W = W - PAD_L - PAD_R
const INNER_H = H - PAD_T - PAD_B

const xLabel = (v: string, xKey: 'date' | 'month'): string =>
  xKey === 'month' ? v : v.slice(5)

/**
 * 迷你纵向双柱图（dashboard_analytics_v1）：纯 SVG，每个刻度 cards/opens 两柱并列，hover 显示数值 tooltip。
 */
const MiniBarChart: React.FC<Props> = ({ data, xKey }) => {
  const [hover, setHover] = useState<number | null>(null)

  const { groups, maxV, xTicks } = useMemo(() => {
    const n = data.length
    const max = Math.max(1, ...data.map((d) => Math.max(d.cards, d.opens)))
    const band = n > 0 ? INNER_W / n : INNER_W // 每个刻度带的宽度
    const barW = Math.min(14, Math.max(3, (band * 0.6) / 2))
    const y = (v: number) => PAD_T + INNER_H - (v / max) * INNER_H
    const list = data.map((d, i) => {
      const cx = PAD_L + band * i + band / 2
      return {
        cx,
        xc: cx - barW - 1,
        xo: cx + 1,
        barW,
        yc: y(d.cards),
        yo: y(d.opens),
        hc: (d.cards / max) * INNER_H,
        ho: (d.opens / max) * INNER_H,
        label: xLabel((xKey === 'month' ? (d as AnalyticsMonthPoint).month : (d as AnalyticsPoint).date) ?? '', xKey),
        raw: xKey === 'month' ? (d as AnalyticsMonthPoint).month : (d as AnalyticsPoint).date,
        cards: d.cards,
        opens: d.opens,
        visitors: d.visitors,
        bandX: PAD_L + band * i,
        bandW: band,
      }
    })
    const tickIdx: number[] = []
    if (n > 0) {
      const wanted = Math.min(5, n)
      for (let k = 0; k < wanted; k++) {
        const idx = Math.round((k * (n - 1)) / Math.max(1, wanted - 1))
        if (!tickIdx.includes(idx)) tickIdx.push(idx)
      }
    }
    return { groups: list, maxV: max, xTicks: tickIdx.map((i) => ({ x: list[i].cx, label: list[i].label })) }
  }, [data, xKey])

  if (groups.length === 0) return null

  const gridVals = [0.25, 0.5, 0.75].map((r) => Math.round(maxV * r))
  const gridY = (v: number) => PAD_T + INNER_H - (v / maxV) * INNER_H

  return (
    <div style={{ position: 'relative', height: 220, borderBottom: '1px solid #f0f0f0', marginBottom: 16 }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '100%' }}>
        <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + INNER_H} stroke="#f0f0f0" />
        <line x1={PAD_L} y1={PAD_T + INNER_H} x2={W - PAD_R} y2={PAD_T + INNER_H} stroke="#f0f0f0" />
        {gridVals.map((v) => (
          <g key={v}>
            <line x1={PAD_L} y1={gridY(v)} x2={W - PAD_R} y2={gridY(v)} stroke="#f0f0f0" strokeDasharray="3 3" />
            <text x={8} y={gridY(v) + 4} fontSize={10} fill="#7F8C8D">{v}</text>
          </g>
        ))}
        <text x={8} y={gridY(0) + 4} fontSize={10} fill="#7F8C8D">0</text>

        {groups.map((g, i) => (
          <g key={i}>
            <rect x={g.xc} y={g.yc} width={g.barW} height={Math.max(0, g.hc)} fill={COLOR_CARDS} rx={1} />
            <rect x={g.xo} y={g.yo} width={g.barW} height={Math.max(0, g.ho)} fill={COLOR_OPENS} rx={1} />
            {/* 命中带 */}
            <rect
              x={g.bandX}
              y={PAD_T}
              width={g.bandW}
              height={INNER_H}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          </g>
        ))}

        {hover !== null && groups[hover] && (
          <line
            x1={groups[hover].cx}
            y1={PAD_T}
            x2={groups[hover].cx}
            y2={PAD_T + INNER_H}
            stroke="#d9d9d9"
            strokeDasharray="3 3"
          />
        )}

        {xTicks.map((t, i) => (
          <text
            key={`${t.label}-${i}`}
            x={t.x}
            y={H - 8}
            fontSize={10}
            fill="#7F8C8D"
            textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}
          >
            {t.label}
          </text>
        ))}
      </svg>

      {hover !== null && groups[hover] && (
        <div
          style={{
            position: 'absolute',
            left: `${(groups[hover].cx / W) * 100}%`,
            top: 0,
            transform: `translateX(${groups[hover].cx / W > 0.6 ? '-110%' : '10%'})`,
            background: 'rgba(0,0,0,0.75)',
            color: '#fff',
            borderRadius: 6,
            padding: '6px 10px',
            fontSize: 12,
            lineHeight: '18px',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            zIndex: 2,
          }}
        >
          <div style={{ fontWeight: 600 }}>{groups[hover].raw}</div>
          <div>留位卡生成数：{groups[hover].cards}</div>
          <div>链接打开数：{groups[hover].opens}</div>
          <div>打开人数：{groups[hover].visitors}</div>
        </div>
      )}
    </div>
  )
}

export default MiniBarChart
